// src/Components/AIMito/ChatMessages.tsx
import React from 'react';
import ChatResponseActions from './ChatResponseActions';

interface ChatMessagesProps {
  messages: string[];
  error: string;
  showHint: boolean;
  loading: boolean;
  suggestions: string[];
  onSuggestionClick: (suggestion: string) => void;
}

// Function to format the message with HTML links and line breaks
const formatMessageWithLinks = (message: string): string => {
  if (message.includes('<a ') || message.includes('<button ')) {
    return message.replace(/\n/g, '<br />'); // keep line-breaks only
  }
  return message
    .replace(/\n/g, '<br />')
    .replace(/(\bhttps?:\/\/[^\s]+)|(\bwww\.[^\s]+)/g, (url) => {
      // Remove any trailing commas from the URL
      const sanitizedUrl = url.replace(/,/g, '');
      const link = sanitizedUrl.startsWith('http')
        ? sanitizedUrl
        : `https://${sanitizedUrl}`;
      return `<a href="${link}" target="_blank" rel="noopener noreferrer" style="text-decoration: underline; color: #007bff;">${sanitizedUrl}</a>`;
    });
};

// Utility to remove HTML tags for TTS
const stripHtmlTags = (html: string): string => {
  return html.replace(/<[^>]*>/g, '');
};

const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  error,
  showHint,
  loading,
  suggestions,
  onSuggestionClick,
}) => (
  <div className="chatbox">
    <div id="chat-content" className="messages">
      {messages.map((msg, index) => {
        const messageId = `ai-response-${index}`;
        // Check if it's an AI response or user message
        const isAIResponse = !msg.startsWith('You');

        return (
          <div
            key={index}
            id={messageId}
            className={`message ${isAIResponse ? 'bot' : 'user'}`}
            style={{ position: 'relative' }}
          >
            {/* Display the message with clickable links */}
            <div
              dangerouslySetInnerHTML={{ __html: formatMessageWithLinks(msg) }}
            />

            {/* Render PDF & TTS actions ONLY for AI responses */}
            {isAIResponse && (
              <ChatResponseActions
                messageId={messageId}
                // Remove the "AI Mito: " prefix and any HTML tags so TTS gets clean text
                message={stripHtmlTags(msg.replace(/^AI Mito:\s*/, ''))}
              />
            )}
          </div>
        );
      })}

      {loading && (
        <div className="message bot typing-indicator">
          AI Mito is typing
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </div>
      )}

      {error && <div className="error">{error}</div>}

      {showHint && messages.length === 0 && (
        <div id="hint" className="hint">
          <ul>
            {suggestions.map((suggestion, index) => (
              <li
                key={index}
                onClick={() => onSuggestionClick(suggestion)}
                style={{ cursor: 'pointer', color: '#399fbe' }}
              >
                {suggestion}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </div>
);

export default ChatMessages;
